/* eslint-disable unicode-bom */
// Tabela de parcelas
import React, { useEffect, useState } from 'react';
import { parcelaListService, ParcelaData, ParcelaSearchParams } from '../../services/parcelaService';
import { formatDate } from '../../utils/formatters';
import { seguradoraService, produtoService, situacaoService, empresaService, statusService } from '../../services/entitiesServices';
import { useNotifier } from '../ui/Notifier';

interface Props {
  onEdit: (p: ParcelaData) => void;
  onView?: (p: ParcelaData) => void;
  onRefresh: () => void;
  onDelete?: (p: ParcelaData) => void;
  // id de uma parcela ativa para highlight/scroll (opcional)
  activeId?: string | null;
  // register a navigator function so parent can ask for next record by id
  // navigator may fetch the next page; it returns a Promise resolving to the next ParcelaData or undefined
  onRegisterNavigator?: (fn: (currentId?: string) => Promise<ParcelaData | undefined>) => void;
  // trigger para refresh da tabela
  refreshTrigger?: number;
}

export const ParcelaTable: React.FC<Props> = ({ onEdit, onView, onRefresh, onDelete, activeId, onRegisterNavigator, refreshTrigger }) => {
  const [items, setItems] = useState<ParcelaData[]>([]);
  const [loading, setLoading] = useState(false);
  const notifier = useNotifier();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('dataVencimento');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filter states (default: mostrar somente parcelas em aberto)
  const [filters, setFilters] = useState<NonNullable<ParcelaSearchParams['filters']>>({ pago: false });
  const [tempFilters, setTempFilters] = useState<NonNullable<ParcelaSearchParams['filters']>>({ pago: false });
  const [showFilters, setShowFilters] = useState(false);

  // keep current endpoint so subsequent loads (pagination, sort, search) use the same source
  const [currentEndpoint, setCurrentEndpoint] = useState<string>('/api/parcelas/em-aberto');
  
  // Catalog data for dropdowns
  const [, setSeguradoras] = useState<any[]>([]);
  const [, setProdutos] = useState<any[]>([]);
  const [, setSituacoes] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [, setEmpresas] = useState<any[]>([]);

  const loadParcelas = async (params?: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc'; filters?: ParcelaSearchParams['filters']; endpoint?: string }) => {
    setLoading(true);
    try {
      let q = (params?.search || '').trim();
      
      const currentSortBy = params?.sortBy || sortBy;
      const currentSortOrder = params?.sortOrder || sortOrder;
      const currentFilters = params?.filters || filters;
      
      // if caller provided an endpoint override, remember it for subsequent calls
      if (params?.endpoint) setCurrentEndpoint(params.endpoint);

      const usedEndpoint = params?.endpoint || currentEndpoint || '/parcelas';

      const resp = await parcelaListService.getParcelas({ 
        search: q || undefined, 
        page: params?.page || page, 
        limit: params?.limit || limit, 
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        filters: currentFilters,
      }, usedEndpoint);
      
      // A API retorna { success: true, data: [...], pagination: {...} }
      setItems(resp.data || []);
      setTotalPages(resp.pagination?.totalPages || resp.totalPages);

      // register navigator after items load
      if (onRegisterNavigator) {
        // capture page for this load
        const currentPage = params?.page || page;

        const fn = async (currentId?: string) => {
          const list = resp.data || [];
          if (!currentId) return list.length > 0 ? list[0] : undefined;
          const idx = list.findIndex((x: ParcelaData) => x.id === currentId);
          // if next item exists on the same page, return it
          if (idx >= 0 && idx + 1 < list.length) return list[idx + 1];
          // if this is the last item on current page AND there are more pages, load next page
          const totalPgs = resp.pagination?.totalPages || resp.totalPages;
          if (idx >= 0 && idx === list.length - 1 && totalPgs && currentPage < totalPgs) {
            // trigger table to load next page
            const nextPageNum = currentPage + 1;
            setTimeout(() => handlePageChange(nextPageNum), 50); // small delay to close modal first
            // return undefined to close modal - table will show next page
            return undefined;
          }
          // if we're at the last item of the last page, just close modal
          return undefined;
        };
        onRegisterNavigator(fn);
      }
    } catch (e) {
      console.error('❌ PARCELA ERROR:', e);
      notifier.show({ message: 'Erro ao carregar parcelas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogs = async () => {
    try {
      const [seguradoras, produtos, situacoes, status, empresas] = await Promise.allSettled([
        seguradoraService.list({ limit: 1000 }),
        produtoService.list({ limit: 1000 }),
        situacaoService.list({ limit: 1000 }),
        statusService.list({ limit: 1000 }),
        empresaService.list({ limit: 1000 })
      ]);

      if (seguradoras.status === 'fulfilled') {
        setSeguradoras(seguradoras.value?.data || []);
      }
      if (produtos.status === 'fulfilled') {
        setProdutos(produtos.value?.data || []);
      }
      if (situacoes.status === 'fulfilled') {
        setSituacoes(situacoes.value?.data || []);
      }
      if (status.status === 'fulfilled') {
        setStatuses(status.value?.data || []);
      }
      if (empresas.status === 'fulfilled') {
        setEmpresas(empresas.value?.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar catálogos:', error);
    }
  };

  useEffect(() => {
    loadCatalogs();
    // initial load will use the default currentEndpoint (em-aberto)
    loadParcelas({ page: 1, limit: limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadParcelas({ page, limit, search: searchTerm, sortBy, sortOrder, filters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleSort = (field: string) => {
    if (field === sortBy) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      loadParcelas({ page: 1, limit: limit, search: searchTerm, sortBy: field, sortOrder: newOrder, filters });
    } else {
      setSortBy(field);
      setSortOrder('asc');
      loadParcelas({ page: 1, limit: limit, search: searchTerm, sortBy: field, sortOrder: 'asc', filters });
    }
    setPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    // When user performs a search, switch to the 'all parcelas' endpoint
    loadParcelas({ page: 1, limit: limit, search: searchTerm, filters, endpoint: '/parcelas' });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadParcelas({ page: newPage, limit: limit, search: searchTerm, filters });
  };

  const handleTempFilterChange = (key: keyof NonNullable<ParcelaSearchParams['filters']>, value: string | boolean | undefined) => {
    const newTempFilters = { ...tempFilters, [key]: value };
    setTempFilters(newTempFilters);
  };

  const handleTempDateFilterChange = (dateField: 'dataVencimento' | 'dataPagamento', rangeType: 'gte' | 'lte' | 'gt' | 'lt', value: string) => {
    const newTempFilters = {
      ...tempFilters,
      [dateField]: {
        ...(tempFilters[dateField] || {}),
        [rangeType]: value || undefined
      }
    };
    // Clean up empty date objects
    if (!newTempFilters[dateField] || Object.values(newTempFilters[dateField]!).every(v => !v)) {
      delete newTempFilters[dateField];
    }
    setTempFilters(newTempFilters);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    // When applying filters, switch to the 'all parcelas' endpoint
    loadParcelas({ page: 1, limit: limit, search: searchTerm, filters: tempFilters, endpoint: '/parcelas' });
  };

  const clearFilters = () => {
    setFilters({});
    setTempFilters({});
    setPage(1);
    loadParcelas({ page: 1, limit: limit, search: searchTerm, filters: {} });
  };

  if (loading) return <div className="p-3 text-center text-xs">Carregando parcelas...</div>;

  return (
    <div className="space-y-3">
      {/* Header e Busca */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Parcelas ({items.length})
        </h2>
        
        <div className="flex gap-1 flex-wrap">
          {/* Campo de ordenação */}
          <div className="flex items-center gap-1">
            <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
            <select
              value={sortBy}
              onChange={(e) => handleSort(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '140px'
              }}
            >
              <option value="numeroDocumento">Documento</option>
              <option value="numeroParcela">Parcela</option>
              <option value="dataVencimento">Vencimento</option>
              <option value="dataPagamento">Pagamento</option>
              <option value="pago">Situação</option>
              <option value="cliente">Cliente</option>
              <option value="status">Status</option>
              <option value="situacao">Situação</option>
              <option value="seguradora">Seguradora</option>
              <option value="produto">Produto</option>
            </select>
            <button
              onClick={() => {
                const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                setSortOrder(newOrder);
                loadParcelas({ page: 1, limit, search: searchTerm, sortBy, sortOrder: newOrder, filters });
                setPage(1);
              }}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="flex gap-1">
            <input
              type="text"
              placeholder="Buscar parcelas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '160px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-offset-1 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-2 py-1 text-xs font-medium border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
          >
            Filtros {showFilters ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 p-3 rounded-md border filters-panel" style={{ borderColor: '#C3B4DE', overflow: 'visible', position: 'relative' }}>
          <style>{`
            .filter-select {
              -webkit-appearance: menulist !important;
              -moz-appearance: menulist !important;
              appearance: auto !important;
              background-image: none !important;
              background-color: white !important;
            }
            .filters-panel {
              overflow: visible !important;
              position: relative !important;
            }
            .filter-select:focus {
              outline: none !important;
              border-color: #7A61B2 !important;
              box-shadow: 0 0 0 1px #7A61B2 !important;
            }
            .filter-select option {
              padding: 4px 8px !important;
              font-size: 12px !important;
              background: white !important;
              color: black !important;
            }
          `}</style>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            
            {/* Pago */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Situação Pagamento
              </label>
              <select
                value={tempFilters.pago === undefined ? '' : tempFilters.pago.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  handleTempFilterChange('pago', value === '' ? undefined : value === 'true');
                }}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todas</option>
                <option value="true">Pagas</option>
                <option value="false">Em Aberto</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Status
              </label>
              <select
                value={tempFilters.statusId || ''}
                onChange={(e) => handleTempFilterChange('statusId', e.target.value || undefined)}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todos</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>

            {/* Data Vencimento - span full width */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Data Vencimento
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">De</label>
                  <input
                    type="date"
                    value={tempFilters.dataVencimento?.gte || ''}
                    onChange={(e) => handleTempDateFilterChange('dataVencimento', 'gte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Até</label>
                  <input
                    type="date"
                    value={tempFilters.dataVencimento?.lte || ''}
                    onChange={(e) => handleTempDateFilterChange('dataVencimento', 'lte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
              </div>
            </div>

            {/* Data Pagamento - span full width */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Data Pagamento
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">De</label>
                  <input
                    type="date"
                    value={tempFilters.dataPagamento?.gte || ''}
                    onChange={(e) => handleTempDateFilterChange('dataPagamento', 'gte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Até</label>
                  <input
                    type="date"
                    value={tempFilters.dataPagamento?.lte || ''}
                    onChange={(e) => handleTempDateFilterChange('dataPagamento', 'lte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
              </div>
            </div>

          </div>
          
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={clearFilters}
              className="px-3 py-1 text-xs font-medium border rounded-md hover:bg-gray-100"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
            >
              Limpar Filtros
            </button>
            <button
              onClick={applyFilters}
              className="px-3 py-1 text-xs font-medium border rounded-md text-white hover:opacity-90"
              style={{ backgroundColor: '#7A61B2', borderColor: '#7A61B2' }}
            >
              Buscar
            </button>
          </div>
        </div>
      )}

      {/* Tabela de parcelas */}
      {!loading && items.length > 0 && (
        <>
          {/* Cards Grid */}
          <div className="space-y-3">
            {items.map((parcela) => {
              // Determinar status e cor da borda
              const isPago = parcela.pago;
              const isVencido = !isPago && new Date(parcela.dataVencimento) < new Date();
              const isPendente = !isPago && !isVencido;
              
              let borderColor = '#10B981'; // Verde para pago
              let statusBgColor = 'bg-green-50';
              let statusTextColor = 'text-green-700';
              let statusIcon = '✓';
              let statusText = 'Pago';
              
              if (isVencido) {
                borderColor = '#EF4444'; // Vermelho para vencido
                statusBgColor = 'bg-red-50';
                statusTextColor = 'text-red-700';
                statusIcon = '✕';
                statusText = 'Vencido';
              } else if (isPendente) {
                borderColor = '#F59E0B'; // Amarelo para pendente
                statusBgColor = 'bg-yellow-50';
                statusTextColor = 'text-yellow-700';
                statusIcon = '⏱';
                statusText = 'Pendente';
              }
              
              return (
                <div 
                  key={parcela.id}
                  data-parcela-id={parcela.id}
                  className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow relative overflow-hidden ${activeId === parcela.id ? 'ring-2 ring-indigo-300' : ''}`}
                  style={{ borderLeft: `4px solid ${borderColor}` }}
                >
                  <div className="p-4">
                    {/* Header com Status e Ações */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${statusBgColor} ${statusTextColor}`}>
                          <span>{statusIcon}</span>
                          <span>{statusText}</span>
                        </span>
                        {(parcela as any).coberturaAtiva === false && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                            ⚠ Cobertura Suspensa
                          </span>
                        )}
                        <h3 className="text-sm font-semibold" style={{ color: '#3d2b56' }}>
                          Parcela {parcela.numeroParcela}
                        </h3>
                      </div>
                      
                      {/* Botões de Ação */}
                      <div className="flex items-center gap-1">
                        {onView && (
                          <button 
                            onClick={() => onView(parcela)} 
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors" 
                            title="Ver Detalhes"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        <button 
                          onClick={() => onEdit(parcela)} 
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                          title="Editar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {onDelete && (
                          <button 
                            onClick={() => onDelete(parcela)} 
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors" 
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Grid de Informações */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                      {/* Documento */}
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Documento</div>
                          <div className="font-semibold text-gray-900 truncate">{parcela.numeroDocumento || '-'}</div>
                        </div>
                      </div>

                      {/* Vencimento */}
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Vencimento</div>
                          <div className="font-medium text-gray-900">{formatDate(parcela.dataVencimento)}</div>
                        </div>
                      </div>

                      {/* Seguradora */}
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Seguradora</div>
                          <div className="font-medium text-gray-900 truncate">{parcela.documento?.seguradora?.nome || '-'}</div>
                        </div>
                      </div>

                      {/* Cliente */}
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Cliente</div>
                          <div className="font-semibold text-gray-900 truncate">{parcela.documento?.cliente?.nome || '-'}</div>
                        </div>
                      </div>

                      {/* Pagamento */}
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Pagamento</div>
                          <div className="font-medium text-gray-900">{parcela.dataPagamento ? formatDate(parcela.dataPagamento) : '—'}</div>
                        </div>
                      </div>

                      {/* Produto */}
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Produto</div>
                          <div className="font-medium text-gray-900 truncate">{parcela.documento?.produto?.nome || '-'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Status e Situação badges no rodapé */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-gray-500 uppercase" style={{ fontSize: '11px' }}>Status:</span>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {parcela.documento?.status?.nome || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-gray-500 uppercase" style={{ fontSize: '11px' }}>Situação:</span>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {parcela.documento?.situacao?.nome || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Paginação */}
          {totalPages && totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <div className="text-xs" style={{ color: '#9E9E9E' }}>
                Página {page} de {totalPages}
              </div>
              
              <div className="flex space-x-1">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
                >
                  Anterior
                </button>
                
                <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>
                  Página {page}{totalPages ? ` de ${totalPages}` : ''}
                </span>
                
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}

          {/* Estado vazio */}
          {!loading && items.length === 0 && (
            <div className="mt-4 bg-white rounded-lg shadow p-6 text-center">
              <div className="text-sm text-gray-500 mb-2">Nenhuma parcela encontrada</div>
              <button
                onClick={onRefresh}
                className="text-sm px-4 py-2 border rounded hover:bg-gray-50"
                style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              >
                Atualizar
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};